import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Button } from 'components/ui/button';
import { Plus } from 'lucide-react';
import { PAYMENT_CONFIG, ModalType, PaymentSolutionTab, PaymentDeviceConfig } from 'state/paymentConfig/type';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import AddPaymentDevice from './modals/addPaymentDeviceModal';
import DeleteDevice from './modals/deleteDevice';
import { toast } from 'react-toastify';
import { initialDeviceConfig } from '../atm';
import Table, { ColumnConfig } from 'components/table';
import DeviceRowExpansion from './DeviceRowExpansion';
import ExpandToggleCell from './ExpandToggleCell';
import DeviceActionsCell from './DeviceActionsCell';
import NoDevicesMessage from './NoDevicesMessage';
interface ICommonProps {
  defaultProvider: PAYMENT_CONFIG.IProcessors | undefined;
  activeTab: PaymentSolutionTab;
  localTab: PaymentSolutionTab;
  deviceConfig: Record<string, string | null | undefined>;
  setDeviceConfig: Dispatch<
    SetStateAction<Record<string, string | null | undefined>>
  >;
  showTestButton?: boolean;
}
const DeviceConfig = ({
  defaultProvider,
  activeTab,
  localTab,
  deviceConfig,
  setDeviceConfig,
  showTestButton = true,
}: ICommonProps) => {
  const {
    paymentConfig,
    resetDeviceCreated,
    resetDeviceDelete,
    resetDeviceupdated,
    toGetPaymentDeviceLocation,
    toCreatePaymentDeviceLocation,
    toUpdatePaymentDeviceLocation,
    toTestPaymentDeviceLocation,
    toDeletePaymentDeviceLocation,
  } = usePaymentConfig();
  const {
    paymentDeviceList,
    paymentDeviceCreated,
    paymentDeviceUpdated,
    deviceDeleted,
    paymentConfiguration,
    posLocationList,
  } = paymentConfig;

  const [modal, setAddDeviceModal] = useState(false);

  const [modalType, setModalType] = useState<ModalType>(ModalType.SAVE);

  const [deviceId, setDeviceId] = useState<string>();

  const [deleteDeviceName, setDeleteDeviceName] = useState<string>();

  const [deleteDeviceId, setDeleteDeviceId] = useState<string>();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const editDeviceCall = (
    values:
      | PAYMENT_CONFIG.ICreatePaymentDeviceSimple
      | PAYMENT_CONFIG.ICreatePaymentDeviceFull,
    id: string
  ) => {
    toUpdatePaymentDeviceLocation(values, id);
  };

  const [deviceList, setDeviceList] = useState<
    PAYMENT_CONFIG.IGetPaymentDeviceLocation[] | []
  >([]);

  const emptyDeviceListCheck =
    paymentDeviceList && paymentDeviceList.length > 0 && activeTab === localTab;

  const configDeleteDevice = (id: string, name: string) => {
    setDeleteDeviceName(name);
    setDeleteDeviceId(id);
    setShowDeleteModal(true);
  };

  const configDeviceModal = (show: boolean, type?: ModalType) => {
    if (show && type) {
      setModalType(type);
      setAddDeviceModal(true);
    }
    if (!show) {
      setDeviceConfig(initialDeviceConfig);
      setAddDeviceModal(false);
    }
  };

  const tableRowExpand = {
    className: 'dropdownArea overflow-hidden',
    renderer: (row: PAYMENT_CONFIG.IGetPaymentDeviceLocation) => (
      <DeviceRowExpansion
        row={row}
        deviceConfig={deviceConfig}
        setDeviceConfig={setDeviceConfig}
        paymentConfiguration={paymentConfiguration}
        posLocationList={posLocationList}
        onTestDevice={toTestPaymentDeviceLocation}
        showTestButton={showTestButton}
      />
    ),
    columnRenderer: (expanded: boolean) => <ExpandToggleCell expanded={expanded} />,
  };

  const NoDataIndication = (
    <NoDevicesMessage
      defaultProvider={defaultProvider}
      onAddDevice={() => configDeviceModal(true, ModalType.SAVE)}
    />
  );

  const tableColumns: ColumnConfig<PAYMENT_CONFIG.IGetPaymentDeviceLocation>[] =
    [
      {
        key: 'name',
        label: 'name',
      },
      {
        key: 'id',
        label: 'Actions',
        renderCell: (row) => (
          <DeviceActionsCell
            row={row}
            onEdit={(config, id) => {
              setDeviceConfig({ ...deviceConfig, ...config });
              setDeviceId(id);
              configDeviceModal(true, ModalType.EDIT);
            }}
            onDelete={configDeleteDevice}
          />
        ),
      },
    ];

  // Consolidated device mutation effect
  useEffect(() => {
    if (activeTab !== localTab) return;

    if (paymentDeviceCreated) {
      resetDeviceCreated();
      setAddDeviceModal(false);
      setModalType(ModalType.SAVE);
      toast.success('Payment device added successfully');
      if (defaultProvider?.processorName) {
        toGetPaymentDeviceLocation(defaultProvider.processorName);
      }
    }

    if (paymentDeviceUpdated) {
      resetDeviceupdated();
      setAddDeviceModal(false);
      setDeviceId(undefined);
      setModalType(ModalType.EDIT);
      toast.success('Payment device updated successfully');
      setDeviceConfig({ ...initialDeviceConfig });
      if (defaultProvider?.processorName) {
        toGetPaymentDeviceLocation(defaultProvider.processorName);
      }
    }
  }, [paymentDeviceCreated, paymentDeviceUpdated, activeTab, localTab]);

  useEffect(() => {
    if (deviceDeleted && activeTab === localTab) {
      resetDeviceDelete();
      toast.success('Device deleted successfully');
      setDeleteDeviceId(undefined);
      setDeleteDeviceName(undefined);
      setShowDeleteModal(false);
      if (defaultProvider?.processorName) {
        toGetPaymentDeviceLocation(defaultProvider.processorName);
      }
    }
    resetDeviceDelete();
  }, [deviceDeleted]);

  useEffect(() => {
    if (!modal) {
      setDeviceConfig({ ...initialDeviceConfig });
    }
  }, [modal]);

  useEffect(() => {
    if (emptyDeviceListCheck) {
      setDeviceList(paymentDeviceList);
    } else {
      setDeviceList([]);
    }
  }, [paymentDeviceList]);

  return (
    <>
      {paymentConfiguration?.deviceConfigurationMode !== PaymentDeviceConfig.NONE && (
        <>
          <div
            className="flex justify-between mt-6 max-w-3xl"
            data-testid="payment_device"
          >
            <span className="text-gray-blue-80 font-semibold text-base">
              Payment device configurations
            </span>
            <span>
              <Button
                variant="outline"
                onClick={() => configDeviceModal(true, ModalType.SAVE)}
                className="inline-flex items-center m-0 mb-2 bg-gray-blue-0 border border-gray-blue-20 text-sm text-gray-blue-80 hover:bg-gray-50"
                disabled={!defaultProvider}
              >
                <Plus size={18} className="mr-1" aria-hidden="true" />
                Add a new payment device
              </Button>
            </span>
          </div>
          <div className="max-w-3xl">
            <Table
              data={deviceList}
              columns={tableColumns}
              theme={{
                hideHeaders: true,
                tableGridLayout: '1fr auto auto',
              }}
              rowExpand={tableRowExpand}
              noDataIndication={NoDataIndication}
            />
          </div>
          {modal && paymentConfiguration?.deviceConfigurationMode && (
            <AddPaymentDevice
              type={modalType}
              modal={modal}
              setModal={configDeviceModal}
              submitCall={(
                device:
                  | PAYMENT_CONFIG.ICreatePaymentDeviceSimple
                  | PAYMENT_CONFIG.ICreatePaymentDeviceFull,
                id?: string
              ) =>
                modalType === ModalType.SAVE
                  ? toCreatePaymentDeviceLocation(device)
                  : editDeviceCall(device, id || '')
              }
              deviceId={deviceId}
              processorId={defaultProvider?.processorId || ''}
              deviceConfigType={paymentConfiguration?.deviceConfigurationMode}
              deviceConfig={deviceConfig}
              setDeviceConfig={setDeviceConfig}
              posLocationList={posLocationList}
            />
          )}

          {showDeleteModal && (
            <DeleteDevice
              deleteDevice={(id: string) => toDeletePaymentDeviceLocation(id)}
              showModal={showDeleteModal}
              setShowModal={setShowDeleteModal}
              deviceId={deleteDeviceId ?? ''}
              deviceName={deleteDeviceName ?? ''}
            />
          )}
        </>
      )}
    </>
  );
};

export default DeviceConfig;
